import React, { useEffect, useMemo, useState } from 'react';
import { LogOut, User } from 'lucide-react';
import { BeachvisionLayout } from '../../components/beachvision/BeachvisionLayout';
import { PageErrorBoundary } from '../../components/beachvision/PageErrorBoundary';
import { authService } from '../../services/auth';
import { isStaffUser } from '../../lib/debug-mode';

function AccountInner({ currentUser }) {
  const email = currentUser?.email || currentUser?.identity?.email || '';
  const canUseDebugMode = isStaffUser(currentUser);
  const [debugModeEnabled, setDebugModeEnabled] = useState(
    Boolean(currentUser?.features?.debug_mode)
  );
  const displayName =
    email?.split('@')[0] ||
    currentUser?.username ||
    'Athlete';

  useEffect(() => {
    setDebugModeEnabled(Boolean(currentUser?.features?.debug_mode));
  }, [currentUser?.features?.debug_mode]);

  const debugLabel = useMemo(
    () => (debugModeEnabled ? 'Enabled' : 'Disabled'),
    [debugModeEnabled]
  );

  return (
    <>
      <header className="px-4 pt-6 pb-4 flex items-center gap-3">
        <User className="h-5 w-5 text-beachvision-coral" />
        <h1 className="font-display font-black text-xl tracking-tight text-beachvision-primary-light">
          Account
        </h1>
      </header>

      <div className="px-4 flex flex-col items-center pt-8 pb-4">
        <div className="h-20 w-20 rounded-full bg-beachvision-elevated flex items-center justify-center text-3xl font-display font-black text-beachvision-teal ring-4 ring-beachvision-coral/40">
          {displayName.slice(0, 1).toUpperCase()}
        </div>
        <p className="mt-4 font-display font-bold text-white text-lg">{displayName}</p>
        {email && (
          <p className="text-beachvision-muted text-sm mt-1">{email}</p>
        )}
      </div>

      <div className="px-4 mt-6 space-y-4">
        {canUseDebugMode ? (
          <div className="rounded-2xl bg-beachvision-surface-low p-4 border border-amber-400/20">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-white text-sm font-display font-bold uppercase tracking-wide">
                  Admin debug mode
                </p>
                <p className="text-beachvision-muted text-xs mt-1">
                  Show extra model and analysis diagnostics across Beachvision screens.
                </p>
              </div>
              <button
                type="button"
                aria-pressed={debugModeEnabled}
                onClick={() => {
                  const next = !debugModeEnabled;
                  setDebugModeEnabled(next);
                  authService.setDebugMode(next);
                }}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition ${
                  debugModeEnabled ? 'bg-amber-400/80' : 'bg-beachvision-elevated'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
                    debugModeEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            <p className="mt-2 text-[11px] uppercase tracking-wide text-amber-300 font-display font-bold">
              {debugLabel}
            </p>
          </div>
        ) : (
          <div className="rounded-2xl bg-beachvision-surface-low p-8 text-center">
            <p className="text-beachvision-muted text-sm">Account settings coming soon.</p>
          </div>
        )}
        <button
          type="button"
          onClick={() => authService.logout()}
          className="w-full rounded-2xl border border-red-400/30 bg-red-500/10 px-6 py-4 font-display font-bold uppercase tracking-wide text-red-200 hover:bg-red-500/20 transition inline-flex items-center justify-center gap-2"
        >
          <LogOut className="h-4 w-4" />
          Log out
        </button>
      </div>
    </>
  );
}

export default function AccountPage(props) {
  return (
    <BeachvisionLayout>
      <PageErrorBoundary>
        <AccountInner {...props} />
      </PageErrorBoundary>
    </BeachvisionLayout>
  );
}
